// app/frontend/hooks/useFileUpload.ts

import { useState, useCallback, useMemo, useEffect } from 'react'
import { DirectUpload } from '@rails/activestorage'
import { isAllowedFileType, isFileSizeAllowed } from '@/lib/fileUpload'

export interface PendingFile {
  file: File
  progress: number
  status: 'pending' | 'uploading' | 'error'
  error?: string
  blobSignedId?: string
}

export function useFileUpload() {
  const [pendingFile, setPendingFile] = useState<PendingFile | null>(null)

  // Create object URL for image preview, clean up on change
  const previewUrl = useMemo(() => {
    if (pendingFile?.file.type.startsWith('image/')) {
      return URL.createObjectURL(pendingFile.file)
    }
    return null
  }, [pendingFile?.file])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!isAllowedFileType(file.type)) {
      setPendingFile({
        file,
        progress: 0,
        status: 'error',
        error: 'File type not supported'
      })
      return
    }

    // Validate file size
    if (!isFileSizeAllowed(file.size)) {
      setPendingFile({
        file,
        progress: 0,
        status: 'error',
        error: 'File too large (max 10MB)'
      })
      return
    }

    setPendingFile({
      file,
      progress: 0,
      status: 'pending'
    })
  }, [])

  const handleFileRemove = useCallback(() => {
    setPendingFile(null)
  }, [])

  const uploadFile = useCallback((): Promise<string | null> => {
    if (!pendingFile || pendingFile.status === 'uploading') {
      return Promise.resolve(pendingFile?.blobSignedId || null)
    }

    // Already uploaded? Return existing signed ID
    if (pendingFile.blobSignedId) {
      return Promise.resolve(pendingFile.blobSignedId)
    }

    const { file } = pendingFile

    return new Promise((resolve) => {
      setPendingFile(prev => prev ? { ...prev, status: 'uploading', progress: 0 } : null)

      const delegate = {
        directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100)
              setPendingFile(prev => prev ? { ...prev, progress } : null)
            }
          })
        }
      }

      const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', delegate)

      upload.create((error, blob) => {
        if (error || !blob) {
          setPendingFile(prev => prev ? {
            ...prev,
            status: 'error',
            error: error?.message || 'Upload failed',
            progress: 0
          } : null)
          resolve(null)
        } else {
          setPendingFile(prev => prev ? {
            ...prev,
            status: 'pending',
            blobSignedId: blob.signed_id
          } : null)
          resolve(blob.signed_id)
        }
      })
    })
  }, [pendingFile])

  const clearFile = useCallback(() => {
    setPendingFile(null)
  }, [])

  return {
    pendingFile,
    previewUrl,
    handleFileSelect,
    handleFileRemove,
    uploadFile,
    clearFile
  }
}
